<script lang="ts" setup>
import type { FocusEvent } from '@/objects/generated/classes/FocusEvent';
import ListComp from '../List/ListComp.vue';
import { useGuiStore } from '@/stores';
import { ref, watch } from 'vue';
import type ListCompExpose from '@/objects/ListCompExpose';
import type { GridColumnOptions } from '../Grid/Classes';
import { GridColumnOptionsBuilder } from '../Grid/Classes/GridColumnOptions';
import { isToday } from '@/utilities/dateutils';
import type { IGridRowCssRule } from '../Grid/Classes/IGridRowCssRule';
import DateComp from '../Basic/DateComp.vue';
import DisplayTick from '../Basic/DisplayTick.vue';
import { DropdownItemButton, DropdownItemMenu } from '@/objects';

const props = defineProps<{
    events: FocusEvent[];
}>();

const listComp = ref<ListCompExpose<FocusEvent> | null>(null);

const data = ref<FocusEvent[]>(props.events);

const emit = defineEmits<{
    (e: 'hide', focusevent: FocusEvent): void;
}>();

const addCustomColumns = (columns: GridColumnOptions<FocusEvent>[]) => {
    new GridColumnOptionsBuilder<FocusEvent>('EventFirstStart', 'focus.eventfirststart')
        .ReadOnly()
        .SortDisabled()
        .Size(160, 160)
        .CellRenderer(DateComp, { format: 'DATETIMESHORT' })
        .InsertAtEnd(columns);

    new GridColumnOptionsBuilder<FocusEvent>('NumContestants', 'focus.numcontestants', 'NumContestants', 'number')
        .ReadOnly()
        .SortDisabled()
        .Size(100, 100)
        .InsertAtEnd(columns);

    new GridColumnOptionsBuilder<FocusEvent>('Setup.LiveEnabled', 'focus.liveenabled')
        .ReadOnly()
        .SortDisabled()
        .Size(100, 100)
        .CellRenderer(DisplayTick)
        .InsertAtEnd(columns);

    new GridColumnOptionsBuilder<FocusEvent>('SignupSettings.OpenTo', 'focus.signupcloses')
        .ReadOnly()
        .SortDisabled()
        .Size(160, 160)
        .CellRenderer(DateComp, { format: 'DATETIMESHORT' })
        .CssRule({
            className: 'grid-cell-warning',
            func: (val: Date | null, row: FocusEvent) => val != null && val > (row.EventFirstStart ?? row.Date)!,
        })
        .CssRule({
            className: 'grid-cell-error',
            func: (val: Date | null, row: FocusEvent) => {
                // Create a date object from the row.EventFirstStart or row.Date and add two days
                let datePlusTwoDays = new Date(
                    new Date((row.EventFirstStart ?? row.Date)!).setDate(new Date((row.EventFirstStart ?? row.Date)!).getDate() + 2),
                );
                return val != null && val > datePlusTwoDays;
            },
        })
        .InsertAtEnd(columns);

    new GridColumnOptionsBuilder<FocusEvent>('Organizer.Name', 'eventinfobase.organizerid').ReadOnly().SortDisabled().Insert(columns, 3);
    return columns;
};

/**
 * The row class rules for the grid
 */
const rowClassRules: IGridRowCssRule<FocusEvent>[] = [
    {
        className: 'ag-row-today',
        func: (data: FocusEvent) => (data.Date == null ? false : isToday(data.Date!)),
    },
];

watch(
    () => props.events,
    () => {
        data.value = props.events;
        listComp.value?.dataUpdated();
        setTimeout(() => {
            listComp.value?.resizeColumns();
        }, 200);
    },
    { immediate: true },
);

/**
 * A list of actions for the quick buttons
 */
const actionGroups = [
    DropdownItemMenu.create({
        icon: 'fa-gear',
        translationkey: 'general.actions',
        dropdownItems: [
            DropdownItemButton.create({
                translationkey: 'general.confirm',
                icon: 'fa-check',
                action: () => {
                    console.log('confirm and hide event');
                },
            }),
        ],
    }),
];
</script>

<template>
    <ListComp
        ref="listComp"
        v-model="data"
        :gui="useGuiStore().FocusEvent"
        :readonly="true"
        :select-all="true"
        :hide-default-buttons="true"
        :hide-search-field="true"
        :quick-buttons="actionGroups"
        :listMode="true"
        :customColumns="addCustomColumns"
        :row-class-rules="rowClassRules"
        resizeStrategy="sizeColumnsToFit"
    />
</template>
